import { logger } from '@fiverr-private/obs';
import {
    calculateBasePriceFromFinal,
    BUYABLE_TYPE,
    type PricingConfiguration,
    type PricingContext,
} from '@fiverr-private/price_upfront_core';
import { ActiveFilters } from '@fiverr-private/listing_types';
import { FILTER } from '../../constants';

/**
 * Converts final prices (including fees and taxes) to base prices in the gig_price_range filter.
 * This ensures the backend (go-searcher) receives base prices while users interact with final prices in the UI.
 *
 * Should be called always after currency normalization (normalizeCurrencyInActiveFilters).
 */
export const convertPricesToBasePrices = (
    activeFilters: ActiveFilters | undefined,
    isPriceUpfrontEnabled?: boolean,
    priceUpfrontConfiguration?: PricingConfiguration | null,
    priceUpfrontContext?: PricingContext | null
): ActiveFilters => {
    if (!activeFilters) {
        return {};
    }

    const priceRangeId = FILTER.GIG_PRICE_RANGE.ID;
    const priceRangeFilter = activeFilters[priceRangeId];

    if (!priceRangeFilter || !isPriceUpfrontEnabled || !priceUpfrontConfiguration || !priceUpfrontContext) {
        return activeFilters;
    }

    const [min, max] = priceRangeFilter as [number, number];

    const minBasePrice = Math.max(
        calculateBasePriceFromFinal(
            {
                finalPrice: min,
                buyableType: BUYABLE_TYPE.GIG,
                pricingContext: priceUpfrontContext,
                priceUpfrontConfiguration,
            },
            logger
        ),
        FILTER.GIG_PRICE_RANGE.DEFAULTS.MIN
    );

    let maxBasePrice: number | undefined;

    if (max !== null) {
        maxBasePrice = calculateBasePriceFromFinal(
            {
                finalPrice: max,
                buyableType: BUYABLE_TYPE.GIG,
                pricingContext: priceUpfrontContext,
                priceUpfrontConfiguration,
            },
            logger
        );
    }

    logger.debug({
        message: `convertPricesToBasePrices conversion complete`,
        originalMin: min,
        originalMax: max,
        convertedMin: minBasePrice,
        convertedMax: maxBasePrice ?? max,
    });

    return {
        ...activeFilters,
        [priceRangeId]: [minBasePrice, maxBasePrice ?? max],
    };
};
